import type { TerritoryGeoJSON } from '../services/territoryService';
import { formatPopulation } from './formatPopulation';

export type StandingsRankBy = 'counties' | 'population';

export const empireLeaderMeta = (
    counties: number,
    share: number,
    population: number,
    popShare: number,
): string => {
    const land = `${counties.toLocaleString()} counties · ${Math.round(share * 100)}% land`;
    if (population <= 0) return land;
    return `${land} · ${formatPopulation(population)} people (${Math.round(popShare * 100)}%)`;
};

export interface TerritoryLeaderSnapshot {
    team_name: string;
    color: string;
    counties: number;
    share: number;
    population: number;
    popShare: number;
    isChampion: boolean;
}

/** Leader row for a single date's GeoJSON (timelapse frames, exports). */
export function computeLeaderFromGeoJSON(
    geo: TerritoryGeoJSON,
    rankBy: StandingsRankBy = 'counties',
): TerritoryLeaderSnapshot | null {
    if (!geo.features.length) return null;

    const byTeam = new Map<number, {
        team_name: string;
        color: string;
        counties: number;
        population: number;
    }>();
    const seenCounties = new Set<string>();
    let totalCounties = 0;
    let totalPopulation = 0;

    for (const feature of geo.features) {
        const props = feature.properties;
        const fips = props.county_fips;
        if (seenCounties.has(fips)) continue;
        seenCounties.add(fips);
        totalCounties += 1;

        const pop = Number(props.population || 0);
        totalPopulation += pop;

        const teamId = Number(props.team_id);
        let row = byTeam.get(teamId);
        if (!row) {
            row = {
                team_name: props.team_name || 'Unknown',
                color: props.map_color || props.primary_color || '#888888',
                counties: 0,
                population: 0,
            };
            byTeam.set(teamId, row);
        }
        row.counties += 1;
        row.population += pop;
    }

    const rows = Array.from(byTeam.values()).map((row) => ({
        ...row,
        share: totalCounties > 0 ? row.counties / totalCounties : 0,
        popShare: totalPopulation > 0 ? row.population / totalPopulation : 0,
    }));

    if (!rows.length) return null;

    rows.sort((a, b) => {
        if (rankBy === 'population') {
            return b.population - a.population || b.counties - a.counties || a.team_name.localeCompare(b.team_name);
        }
        return b.counties - a.counties || b.population - a.population || a.team_name.localeCompare(b.team_name);
    });

    const leader = rows[0];
    const isChampion = rows.length === 1 && totalCounties > 0;

    return {
        team_name: leader.team_name,
        color: leader.color,
        counties: leader.counties,
        share: leader.share,
        population: leader.population,
        popShare: leader.popShare,
        isChampion,
    };
}

/** Evenly sample snapshot dates for GIF export (always first + last). */
export function pickKeyDates(dates: string[], maxFrames = 18): string[] {
    if (dates.length <= maxFrames) return [...dates];
    const first = dates[0];
    const last = dates[dates.length - 1];
    const middle = dates.slice(1, -1);
    const slots = maxFrames - 2;
    const step = Math.max(1, Math.ceil(middle.length / slots));
    const sampled = middle.filter((_, i) => i % step === 0).slice(0, slots);
    return [first, ...sampled, last];
}
